/**
 * RecurrenceOptionsInlineEditor — Editor de periodicidades em memória.
 *
 * Variante do `RecurrenceOptionsEditor` projetada para o fluxo de CRIAÇÃO de
 * plano V.1: como o plano ainda não existe (não há id), não chamamos RPCs de
 * persistência por linha. O estado é mantido pelo pai e enviado em sequência
 * logo após o `wl_upsert_plan` retornar o id do plano recém-criado.
 *
 * Visual idêntico ao `RecurrenceOptionsEditor` (header + 5 linhas), exceto
 * pela ausência dos botões Salvar/Trash por linha.
 */
import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useWlMasterRecurringServices,
  type RecurrencePeriod,
} from "@/hooks/wl/useWlRecurrenceOptions";
import { applyCurrencyMask, parseCurrency } from "@/lib/input-masks";

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------
export interface PendingRecurrence {
  recurrence_interval: RecurrencePeriod;
  uaizy_master_service_id: string | null;
  price: number;
  setup_fee: number;
  discount_percent: number | null;
  is_active: boolean;
}

interface Props {
  value: PendingRecurrence[];
  onChange: (next: PendingRecurrence[]) => void;
}

const PERIODS: { value: RecurrencePeriod; label: string }[] = [
  { value: "mensal", label: "Mensal" },
  { value: "bimestral", label: "Bimestral" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
];

const emptyFor = (p: RecurrencePeriod): PendingRecurrence => ({
  recurrence_interval: p,
  uaizy_master_service_id: null,
  price: 0,
  setup_fee: 0,
  discount_percent: null,
  is_active: false,
});

export function RecurrenceOptionsInlineEditor({ value, onChange }: Props) {
  // Mapa periodicidade -> objeto atual (para acesso rápido nas linhas).
  const byPeriod = useMemo(() => {
    const m = new Map<RecurrencePeriod, PendingRecurrence>();
    value.forEach((v) => m.set(v.recurrence_interval, v));
    return m;
  }, [value]);

  // Atualiza/insere periodicidade no array do pai.
  const updatePeriod = (
    period: RecurrencePeriod,
    patch: Partial<PendingRecurrence>,
  ) => {
    const current = byPeriod.get(period) ?? emptyFor(period);
    const next: PendingRecurrence = { ...current, ...patch };
    const others = value.filter((v) => v.recurrence_interval !== period);
    onChange([...others, next]);
  };

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div>
        <Label className="text-sm font-semibold">Periodicidades disponíveis</Label>
        <p className="text-xs text-muted-foreground mt-1">
          Para cada periodicidade, vincule um serviço recorrente do tenant master
          (mesma periodicidade) e defina o preço de cobrança. Para habilitar uma
          periodicidade, ative o switch, selecione o serviço e informe o preço.
          As configurações serão salvas junto com o plano.
        </p>
      </div>

      <div className="space-y-2">
        {PERIODS.map((p) => (
          <InlinePeriodRow
            key={p.value}
            period={p.value}
            periodLabel={p.label}
            current={byPeriod.get(p.value) ?? null}
            onChange={(patch) => updatePeriod(p.value, patch)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linha de periodicidade (in-memory)
// ---------------------------------------------------------------------------
interface RowProps {
  period: RecurrencePeriod;
  periodLabel: string;
  current: PendingRecurrence | null;
  onChange: (patch: Partial<PendingRecurrence>) => void;
}

function InlinePeriodRow({ period, periodLabel, current, onChange }: RowProps) {
  // Carrega apenas serviços do master compatíveis com a periodicidade.
  const services = useWlMasterRecurringServices(period);

  const isActive = current?.is_active ?? false;
  const serviceId = current?.uaizy_master_service_id ?? "";
  const price = current?.price ?? 0;
  const setup = current?.setup_fee ?? 0;
  const discount =
    current?.discount_percent !== null && current?.discount_percent !== undefined
      ? String(current.discount_percent)
      : "";

  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Switch
            checked={isActive}
            onCheckedChange={(v) => onChange({ is_active: v })}
          />
          <span className="text-sm font-medium">{periodLabel}</span>
          {!isActive && (
            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-dashed text-muted-foreground">
              Não configurado
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-6 space-y-1">
          <Label className="text-xs">Serviço recorrente do master</Label>
          <Select
            value={serviceId}
            onValueChange={(v) => onChange({ uaizy_master_service_id: v || null })}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  services.isLoading
                    ? "Carregando…"
                    : services.data && services.data.length > 0
                      ? "Selecionar serviço"
                      : `Nenhum serviço ${periodLabel.toLowerCase()} no master`
                }
              />
            </SelectTrigger>
            <SelectContent>
              {(services.data || []).map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                  {s.recurring_price
                    ? ` — R$ ${Number(s.recurring_price).toFixed(2)}`
                    : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3 space-y-1">
          <Label className="text-xs">Preço por ciclo</Label>
          <Input
            placeholder="R$ 0,00"
            value={price > 0 ? applyCurrencyMask(price) : ""}
            onChange={(e) => onChange({ price: parseCurrency(e.target.value) })}
          />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label className="text-xs">Setup</Label>
          <Input
            placeholder="R$ 0,00"
            value={setup > 0 ? applyCurrencyMask(setup) : ""}
            onChange={(e) => onChange({ setup_fee: parseCurrency(e.target.value) })}
          />
        </div>
        <div className="md:col-span-1 space-y-1">
          <Label className="text-xs">Desc %</Label>
          <Input
            type="number"
            min={0}
            max={100}
            placeholder="0"
            value={discount}
            onChange={(e) =>
              onChange({
                discount_percent:
                  e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />
        </div>
      </div>
    </div>
  );
}
